import React, { useCallback, useState } from 'react';
import Grid from '@material-ui/core/Grid';

import { useStore } from 'core/store';
import { Tooltip } from 'core/components/tooltip';

import useCommunityDriveDetails from './use-community-drive-details';
import useStyles from './community-drive-details-tooltip.styles';

const LoadingText = () => {
  const classes = useStyles();
  return <span className={classes.loading}>[loading]</span>;
};

// Match the existing card convention: anything at/below the 41 minimum reads as "Below 41".
const formatScore = (score) => (score > 40 ? `${score}/100` : 'Below 41/100');

// The most recent term hasn't started, so there's nothing meaningful to show yet.
const NotEffectiveContent = ({ classes }) => (
  <Grid className={classes.container}>
    <span className={classes.singleLine}>Term not effective yet</span>
  </Grid>
);

// A past term whose renewal has already generated: show ONLY the date that renewal was generated
// and the Community Drive score captured on that date (read from the renewal term's record). That
// snapshot — not today's live app score — is what set the next term's discount, so it's the only
// thing that explains this term's place in the chain.
const RenewalSnapshotContent = ({ classes, renewalGeneratedDate, renewalScore }) => (
  <Grid className={classes.container}>
    <span className={classes.note}>Score used to generate the next renewal:</span>
    <Grid className={classes.row}>
      <span className={classes.label}>Renewal generated:</span>
      <span>{renewalGeneratedDate || '—'}</span>
    </Grid>
    <Grid className={classes.row}>
      <span className={classes.label}>Score at that time:</span>
      <span>{formatScore(renewalScore)}</span>
    </Grid>
  </Grid>
);

/**
 * Community Drive detail hint, rendered term-position aware (see #32093):
 *  - 'notEffective'    most recent term, not yet effective  -> "Term not effective yet"
 *  - 'renewalSnapshot' an older term with a renewal after it -> the score captured when that
 *                      renewal generated (read from the renewal term, no BigQuery call)
 *  - 'live'            most recent term, in force            -> the live in-app score from BigQuery
 *
 * Showing live BigQuery data on every term (the previous behavior) confused staff, because that
 * table is keyed by the in-force term's policy_id and only reflects current driving — which can
 * differ from the score that was locked in when an already-generated renewal was rated.
 */
const CommunityDriveDetailsTooltip = ({
  mode,
  driver,
  policyId,
  policyEndDate,
  state,
  renewalGeneratedDate,
  renewalScore
}) => {
  const classes = useStyles();
  const { account: accountStore } = useStore();
  const [hasFetched, setHasFetched] = useState(false);

  const { fetchDetails, details, loading, error } = useCommunityDriveDetails({
    policyId,
    driverId: driver?.id,
    policyEndDate,
    state
  });

  const enrollmentTask = accountStore.tasks?.find(
    (task) => task.taskName === 'Community Drive Registration' && task.data?.driverId === driver?.id
  );
  const enrollmentCompleted = !!enrollmentTask?.completedDate;

  // Only the live view hits BigQuery, and only on first hover.
  const handleMouseEnter = useCallback(() => {
    if (mode === 'live' && !hasFetched) {
      setHasFetched(true);
      fetchDetails();
    }
  }, [mode, hasFetched, fetchDetails]);

  // The not-effective and renewal-snapshot views are fully derived from policy records already
  // loaded in the FE — no BigQuery call needed, so render them directly.
  if (mode === 'notEffective') {
    return <Tooltip text={<NotEffectiveContent classes={classes} />} placement="right-start" />;
  }
  if (mode === 'renewalSnapshot') {
    return (
      <Tooltip
        text={
          <RenewalSnapshotContent
            classes={classes}
            renewalGeneratedDate={renewalGeneratedDate}
            renewalScore={renewalScore}
          />
        }
        placement="right-start"
      />
    );
  }

  // mode === 'live': the most recent term is in force, so show what the driver currently sees in
  // the app (the live BigQuery score / trip progress), lazily loaded on hover.
  // Render an async value: dash before hover, spinner while loading, an explicit "Error" when the
  // query failed (so it can't be mistaken for a legitimate empty/"No" result).
  const renderAsync = (value) => {
    if (!hasFetched) {
      return '—';
    }
    if (loading) {
      return <LoadingText />;
    }
    if (error) {
      return <span className={classes.error}>Error</span>;
    }
    return value;
  };

  const tooltipContent = (
    <Grid className={classes.container}>
      <span className={classes.note}>What the driver currently sees in the app:</span>
      <Grid className={classes.row}>
        <span className={classes.label}>Current score:</span>
        <span>{renderAsync(details?.premiumScore != null ? `${details.premiumScore}/100` : '—')}</span>
      </Grid>
      <Grid className={classes.row}>
        <span className={classes.label}>Trip count:</span>
        <span>{renderAsync(details?.tripCount != null ? details.tripCount : '—')}</span>
      </Grid>
      <Grid className={classes.row}>
        <span className={classes.label}>Score update date:</span>
        <span>{renderAsync(details?.scoreUpdateDate || '—')}</span>
      </Grid>
      <Grid className={classes.row}>
        <span className={classes.label}>Scoring cutoff:</span>
        <span>{renderAsync(details?.scoringCutoffDate || '—')}</span>
      </Grid>
      <Grid className={classes.row}>
        <span className={classes.label}>Enrollment completed:</span>
        <span>{enrollmentCompleted ? 'Yes' : 'No'}</span>
      </Grid>
      <Grid className={classes.row}>
        <span className={classes.label}>In data warehouse:</span>
        <span>{renderAsync(details?.presentInWarehouse ? 'Yes' : 'No')}</span>
      </Grid>
    </Grid>
  );

  return <Tooltip text={tooltipContent} onMouseEnter={handleMouseEnter} placement="right-start" />;
};

export default CommunityDriveDetailsTooltip;
